import styled from '@emotion/styled';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { useRef } from 'react';

const TabsContainer = styled(Tabs, {
    shouldForwardProp: prop => prop !== 'maxWidth',
})<{ maxWidth?: string }>`
    display: flex;
    flex-wrap: wrap;
    min-height: 36px;
    max-width: ${({ maxWidth }) => maxWidth || 'unset'};
`;

const StyledTab = styled(Tab, { shouldForwardProp: prop => prop !== 'active' })<{ active?: boolean }>`
    display: flex;
    padding: 6px 16px;
    justify-content: center;
    align-items: center;
    min-height: 32px;
    margin-right: 6px;
    min-width: unset;

    border-radius: 8px;
    background: ${({ active, theme }) => (active ? theme.colors.secondary[900] : theme.colors.black[200])};

    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    letter-spacing: 0.056px;
    text-transform: none;
    color: ${({ active, theme }) => (active ? theme.colors.white : theme.colors.secondary[700])} !important;

    &:hover {
        background: ${({ active, theme }) => (active ? theme.colors.secondary[700] : theme.colors.black[300])};
    }
`;

interface TabProps {
    id: string;
    label: string;
    active: boolean;
    dataTestId?: string;
}

interface Props {
    tabs: TabProps[];
    maxWidth?: string;
    activeTab: string;
    onTabChange: (id: string) => void;
    className?: string;
}

export const ChipFilter = ({ tabs, maxWidth, activeTab, onTabChange, className }: Props) => {
    const tabsRef = useRef<HTMLDivElement>(null);
    let scrollerRef: any;

    const scroll = (target: EventTarget & HTMLDivElement) => {
        if (!scrollerRef) {
            scrollerRef = tabsRef.current?.getElementsByClassName('MuiTabs-scroller')[0];
        }

        if (scrollerRef) {
            scrollerRef.scrollTo({ left: target.offsetLeft - target.offsetWidth, behavior: 'smooth' });
        }
    };

    const handleChange = (event: React.SyntheticEvent, id: string) => {
        onTabChange(id);
        scroll(event.currentTarget as HTMLDivElement);
    };

    const tabsIndex = tabs.findIndex(tab => tab.id === activeTab);

    return (
        <TabsContainer
            value={tabsIndex === -1 ? 0 : tabsIndex}
            variant="scrollable"
            scrollButtons={false}
            sx={{ '& .MuiTabs-indicator': { display: 'none' } }}
            ref={tabsRef}
            maxWidth={maxWidth}
            className={className}>
            {tabs.map(tab => (
                <StyledTab
                    key={tab.id}
                    label={tab.label}
                    active={activeTab === tab.id}
                    data-testid={tab.dataTestId}
                    onClick={event => handleChange(event, tab.id)}
                />
            ))}
        </TabsContainer>
    );
};
